import { createApiClient, type ApiClient, type Tokens } from '@ddt/api-client';
import { MemoryStorage, jsonStore, safeBrowserStorage, type KeyValueStorage } from '@ddt/platform';
import createClient, { type Client } from 'openapi-fetch';
import type { paths as BalancePaths } from './gen/balance';
import type { paths as SimulatorPaths } from './gen/simulator';

export const SESSION_KEY = 'ddt.admin.session.v1';

export interface Services {
  /** Auth pelo Gateway: login, renovação e perfil. */
  api: ApiClient;
  /** Endereço do Gateway (prefixo /api), usado pelas telas de administração do jogo. */
  authBase: string;
  /** Balance Admin API, com o mesmo token e a mesma renovação. */
  balance: Client<BalancePaths>;
  /** Simulador do Match, pelo Gateway (`/api/v1/admin/simulator`), com o mesmo token. */
  sim: Client<SimulatorPaths>;
  /** Chamado quando a sessão não pode mais ser renovada; o painel volta para o login. */
  sessionExpired: { handler: () => void };
}

export interface ServiceOptions {
  authBase?: string;
  balanceBase?: string;
  storage?: KeyValueStorage;
  fetch?: typeof fetch;
}

function isTokens(value: unknown): value is Tokens {
  const v = value as Partial<Tokens> | null;
  return typeof v === 'object' && v !== null && typeof v.accessToken === 'string' && typeof v.refreshToken === 'string' && typeof v.accessExpiresAt === 'number';
}

/**
 * A sessão do painel vale só na aba (sessionStorage): fechar a aba encerra o acesso. Se o navegador bloquear o
 * armazenamento, cai para memória (recarregar pede o login de novo).
 */
function tabStorage(): KeyValueStorage {
  try {
    return safeBrowserStorage(globalThis.sessionStorage);
  } catch {
    return new MemoryStorage();
  }
}

export function createServices(options: ServiceOptions = {}): Services {
  const authBase = options.authBase ?? import.meta.env.VITE_API_BASE ?? 'http://localhost:5000/api';
  const balanceBase = (options.balanceBase ?? import.meta.env.VITE_BALANCE_API ?? 'http://localhost:5020').replace(/\/+$/, '');
  const tokens = jsonStore<Tokens>(options.storage ?? tabStorage(), SESSION_KEY, isTokens);
  const sessionExpired = { handler: () => undefined as void };
  const api = createApiClient({ baseUrl: authBase, tokens, fetch: options.fetch, onSessionExpired: () => sessionExpired.handler() });
  const balance = createClient<BalancePaths>({ baseUrl: balanceBase, fetch: api.authorizedFetch });
  const sim = createClient<SimulatorPaths>({ baseUrl: authBase.replace(/\/+$/, ''), fetch: api.authorizedFetch });
  return { api, authBase, balance, sim, sessionExpired };
}

let current: Services | null = null;

export function getServices(): Services {
  current ??= createServices();
  return current;
}

/** Troca os serviços (testes e inicialização). */
export function setServices(services: Services | null): void {
  current = services;
}
